import { db } from '@/lib/firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  query,
  where,
  getDocs,
  serverTimestamp,
  Timestamp,
  runTransaction,
} from 'firebase/firestore';
import {
  AttendanceRecord,
  CheckInData,
  CheckOutData,
  AbsentReason,
  AttendanceStatus,
} from '@/types/attendance';
import { checkIfLate } from '@/lib/locationUtils';
import { toast } from 'sonner';
import { format } from 'date-fns';

/**
 * Check in user for today's attendance
 * @param data Check-in data with location and office details
 * @returns Created/updated attendance record
 */
export async function checkIn(data: CheckInData): Promise<AttendanceRecord> {
  try {
    // Validate required manual check-in reasons (Issue 11)
    if (data.manualCheckIn && !data.isWithinRange && !data.locationReason) {
      throw new Error('Location reason is required for manual check-in');
    }
    if (data.isLate && !data.lateReason) {
      throw new Error('Late reason is required when checking in late');
    }

    const attendanceId = `${data.userId}_${data.date}`;
    const attendanceRef = doc(db, 'attendance', attendanceId);

    // Use transaction to prevent race conditions (Issues 1, 12)
    const result = await runTransaction(db, async (transaction) => {
      const existingDoc = await transaction.get(attendanceRef);
      
      if (existingDoc.exists() && existingDoc.data().checkInTime) {
        throw new Error('You have already checked in today');
      }

      const attendanceData: Partial<AttendanceRecord> = {
        id: attendanceId,
        userId: data.userId,
        userName: data.userName || data.userId,
        date: data.date,
        officeId: data.officeId,
        officeName: data.officeName || data.officeId,
        status: data.isLate ? 'late' : 'present',
        checkInTime: serverTimestamp() as any, // Use server timestamp (Issue 15)
        checkInLocation: data.location,
        checkInWithinRange: data.isWithinRange,
        checkInDistance: data.distance,
        isLate: data.isLate,
        manualCheckIn: data.manualCheckIn || false,
        createdAt: serverTimestamp() as any,
        updatedAt: serverTimestamp() as any,
      };

      // Only add optional fields if they have values
      if (data.lateReason) attendanceData.lateReason = data.lateReason;
      if (data.locationReason) attendanceData.locationReason = data.locationReason;
      if (data.manualCheckInReason) attendanceData.manualCheckInReason = data.manualCheckInReason;
      if (data.manualCheckInRemarks) attendanceData.manualCheckInRemarks = data.manualCheckInRemarks;

      transaction.set(attendanceRef, attendanceData, { merge: true });
      
      return attendanceData;
    });

    toast.success(
      data.isLate 
        ? 'Checked in (Late)'
        : 'Checked in successfully'
    );

    return { ...result, createdAt: new Date(), updatedAt: new Date() } as AttendanceRecord;
  } catch (error: any) {
    console.error('Error checking in:', error);
    toast.error(error.message || 'Failed to check in');
    throw error;
  }
}

/**
 * Check out user for today's attendance
 * @param userId User ID
 * @param data Check-out data with location
 * @returns Updated attendance record
 */
export async function checkOut(userId: string, data: CheckOutData): Promise<AttendanceRecord> {
  try {
    const today = format(new Date(), 'yyyy-MM-dd');
    const attendanceId = `${userId}_${today}`;
    const attendanceRef = doc(db, 'attendance', attendanceId);

    // Use transaction to prevent race conditions (Issues 1, 12)
    const result = await runTransaction(db, async (transaction) => {
      const existingDoc = await transaction.get(attendanceRef);
      
      if (!existingDoc.exists() || !existingDoc.data().checkInTime) {
        throw new Error('You must check in before checking out');
      }

      if (existingDoc.data().checkOutTime) {
        throw new Error('You have already checked out today');
      }

      // Validate check-out time is after check-in time (Issue 10)
      const checkInTime = existingDoc.data().checkInTime;
      const checkInDate = checkInTime instanceof Timestamp ? checkInTime.toDate() : new Date(checkInTime);
      
      if (data.checkOutTime < checkInDate) {
        throw new Error('Check-out time cannot be before check-in time');
      }

      const updateData: Partial<AttendanceRecord> = {
        checkOutTime: serverTimestamp() as any, // Use server timestamp (Issue 15)
        checkOutLocation: data.checkOutLocation,
        checkOutWithinRange: data.isWithinRange,
        checkOutDistance: data.distance,
        updatedAt: serverTimestamp() as any,
      };

      transaction.set(attendanceRef, updateData, { merge: true });
      
      return { ...existingDoc.data(), ...updateData };
    });

    toast.success('Checked out successfully');

    return { id: attendanceId, ...result } as AttendanceRecord;
  } catch (error: any) {
    console.error('Error checking out:', error);
    toast.error(error.message || 'Failed to check out');
    throw error;
  }
}

/**
 * Mark user as absent for today
 * @param userId User ID
 * @param reason Absent reason
 * @param remarks Optional remarks
 * @returns Created attendance record
 */
export async function markAbsent(
  userId: string,
  userName: string,
  officeId: string,
  officeName: string,
  reason: AbsentReason,
  remarks?: string
): Promise<AttendanceRecord> {
  try {
    const today = format(new Date(), 'yyyy-MM-dd');
    const attendanceId = `${userId}_${today}`;
    const attendanceRef = doc(db, 'attendance', attendanceId);

    // Check if already marked attendance
    const existingDoc = await getDoc(attendanceRef);
    if (existingDoc.exists()) {
      const data = existingDoc.data();
      if (data.checkInTime) {
        throw new Error('You have already checked in today');
      }
      if (data.status === 'absent') {
        throw new Error('You have already marked absent for today');
      }
    }

    const attendanceData: Partial<AttendanceRecord> = {
      id: attendanceId,
      userId,
      userName,
      date: today,
      officeId,
      officeName,
      status: 'absent',
      absentReason: reason,
      absentRemarks: remarks,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(attendanceRef, attendanceData, { merge: true });

    toast.success('Marked as absent');

    return { ...attendanceData, createdAt: new Date(), updatedAt: new Date() } as AttendanceRecord;
  } catch (error: any) {
    console.error('Error marking absent:', error);
    toast.error(error.message || 'Failed to mark absent');
    throw error;
  }
}

/**
 * Auto-mark holiday attendance for today.
 * Called when user opens the app on a holiday. Silent — no toasts.
 * @returns true if holiday was marked
 */
export async function markHolidayAttendance(
  userId: string,
  userName: string,
  officeId: string,
  officeName: string,
  holidayReason?: string
): Promise<boolean> {
  try {
    const today = format(new Date(), 'yyyy-MM-dd');
    const attendanceId = `${userId}_${today}`;
    const attendanceRef = doc(db, 'attendance', attendanceId);

    // Already has a record → do nothing
    const existing = await getDoc(attendanceRef);
    if (existing.exists()) return false;

    const attendanceData: Partial<AttendanceRecord> = {
      id: attendanceId,
      userId,
      userName,
      date: today,
      officeId,
      officeName,
      status: 'holiday' as any,
      absentRemarks: holidayReason || 'Holiday',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(attendanceRef, attendanceData, { merge: true });
    return true;
  } catch (error) {
    console.error('Error marking holiday attendance:', error);
    return false;
  }
}

/**
 * Auto-mark absent if shift has ended and no attendance record exists.
 * Called client-side on page load. Silent — no toasts.
 * @returns true if auto-absent was applied
 */
export async function autoMarkAbsentAfterShift(
  userId: string,
  userName: string,
  officeId: string,
  officeName: string,
  shiftEndTime: string // "HH:mm"
): Promise<boolean> {
  try {
    const today = format(new Date(), 'yyyy-MM-dd');
    const attendanceId = `${userId}_${today}`;
    const attendanceRef = doc(db, 'attendance', attendanceId);

    // Already has a record → do nothing
    const existing = await getDoc(attendanceRef);
    if (existing.exists()) return false;

    // Parse shift end time
    const [endH, endM] = shiftEndTime.split(':').map(Number);
    const now = new Date();
    const shiftEnd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), endH, endM);

    // Add 30-minute buffer after shift end to allow late check-ins
    const bufferMs = 30 * 60 * 1000;
    if (now.getTime() <= shiftEnd.getTime() + bufferMs) return false;

    const attendanceData: Partial<AttendanceRecord> = {
      id: attendanceId,
      userId,
      userName,
      date: today,
      officeId,
      officeName,
      status: 'absent',
      absentReason: 'other',
      absentRemarks: 'Auto-marked absent (shift ended)',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };

    await setDoc(attendanceRef, attendanceData, { merge: true });
    return true;
  } catch (error) {
    console.error('Error auto-marking absent:', error);
    return false;
  }
}

/**
 * Get attendance record for a specific date
 * @param userId User ID
 * @param date Date in YYYY-MM-DD format
 * @returns Attendance record or null if not found
 */
export async function getAttendanceForDate(
  userId: string,
  date: string
): Promise<AttendanceRecord | null> {
  try {
    const attendanceId = `${userId}_${date}`;
    const attendanceRef = doc(db, 'attendance', attendanceId);
    const attendanceDoc = await getDoc(attendanceRef);

    if (!attendanceDoc.exists()) {
      return null;
    }

    return { id: attendanceDoc.id, ...attendanceDoc.data() } as AttendanceRecord;
  } catch (error) {
    console.error('Error fetching attendance:', error);
    return null;
  }
}

/**
 * Get attendance record for today
 * @param userId User ID
 * @returns Today's attendance record or null
 */
export async function getTodayAttendance(userId: string): Promise<AttendanceRecord | null> {
  const today = format(new Date(), 'yyyy-MM-dd');
  
  // First try with the direct ID format
  let attendance = await getAttendanceForDate(userId, today);
  
  // If not found, try querying by userId field as fallback
  if (!attendance) {
    try {
      const attendanceQuery = query(
        collection(db, 'attendance'),
        where('userId', '==', userId),
        where('date', '==', today)
      );
      const querySnapshot = await getDocs(attendanceQuery);
      
      if (!querySnapshot.empty) {
        const doc = querySnapshot.docs[0];
        attendance = { id: doc.id, ...doc.data() } as AttendanceRecord;
      }
    } catch (error) {
      console.error('Error querying attendance:', error);
    }
  }
  
  return attendance;
}

/**
 * Get attendance records for a date range
 * @param userId User ID
 * @param startDate Start date in YYYY-MM-DD format
 * @param endDate End date in YYYY-MM-DD format
 * @returns Array of attendance records
 */
export async function getAttendanceRange(
  userId: string,
  startDate: string,
  endDate: string
): Promise<AttendanceRecord[]> {
  try {
    const attendanceQuery = query(
      collection(db, 'attendance'),
      where('userId', '==', userId),
      where('date', '>=', startDate),
      where('date', '<=', endDate)
    );

    const querySnapshot = await getDocs(attendanceQuery);
    const records: AttendanceRecord[] = [];

    querySnapshot.forEach((doc) => {
      records.push({ id: doc.id, ...doc.data() } as AttendanceRecord);
    });

    return records.sort((a, b) => b.date.localeCompare(a.date));
  } catch (error) {
    console.error('Error fetching attendance range:', error);
    return [];
  }
}

/**
 * Get all attendance records for a date range (for all users)
 * @param startDate Start date in YYYY-MM-DD format
 * @param endDate End date in YYYY-MM-DD format
 * @returns Array of attendance records
 */
export async function getAllAttendanceForDateRange(
  startDate: string,
  endDate: string
): Promise<AttendanceRecord[]> {
  try {
    const attendanceQuery = query(
      collection(db, 'attendance'),
      where('date', '>=', startDate),
      where('date', '<=', endDate)
    );

    const querySnapshot = await getDocs(attendanceQuery);
    const records: AttendanceRecord[] = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      records.push({
        id: doc.id,
        ...data,
        checkInTime: data.checkInTime?.toDate?.() || data.checkInTime,
        checkOutTime: data.checkOutTime?.toDate?.() || data.checkOutTime,
        createdAt: data.createdAt?.toDate?.() || data.createdAt,
        updatedAt: data.updatedAt?.toDate?.() || data.updatedAt,
      } as AttendanceRecord);
    });

    return records.sort((a, b) => b.date.localeCompare(a.date));
  } catch (error) {
    console.error('Error fetching all attendance for date range:', error);
    return [];
  }
}

/**
 * Get all attendance records for a user
 * @param userId User ID
 * @returns Array of all attendance records
 */
export async function getAllUserAttendance(userId: string): Promise<AttendanceRecord[]> {
  try {
    const attendanceQuery = query(
      collection(db, 'attendance'),
      where('userId', '==', userId)
    );

    const querySnapshot = await getDocs(attendanceQuery);
    const records: AttendanceRecord[]  = [];

    querySnapshot.forEach((doc) => {
      records.push({ id: doc.id, ...doc.data() } as AttendanceRecord);
    });

    return records.sort((a, b) => b.date.localeCompare(a.date));
  } catch (error) {
    console.error('Error fetching all attendance:', error);
    return [];
  }
}

/**
 * Check if user can check in (not already checked in or absent)
 * @param userId User ID
 * @returns Boolean indicating if check-in is allowed
 */
export async function canCheckIn(userId: string): Promise<boolean> {
  const todayAttendance = await getTodayAttendance(userId);
  
  if (!todayAttendance) {
    return true; // No record yet, can check in
  }

  // Can't check in if already checked in, marked absent, or holiday
  return !todayAttendance.checkInTime && todayAttendance.status !== 'absent' && todayAttendance.status !== 'holiday';
}

/**
 * Check if user can check out (checked in but not checked out)
 * @param userId User ID
 * @returns Boolean indicating if check-out is allowed
 */
export async function canCheckOut(userId: string): Promise<boolean> {
  const todayAttendance = await getTodayAttendance(userId);
  
  if (!todayAttendance) {
    return false; // No record, must check in first
  }

  // Can check out only if checked in and not yet checked out
  return !!todayAttendance.checkInTime && !todayAttendance.checkOutTime;
}

/**
 * Get attendance status summary for display
 * @param userId User ID
 * @returns Status object with check-in/out times and status
 */
export async function getAttendanceStatus(userId: string): Promise<{
  hasCheckedIn: boolean;
  hasCheckedOut: boolean;
  isAbsent: boolean;
  status: AttendanceStatus;
  checkInTime?: Date;
  checkOutTime?: Date;
  isLate?: boolean;
  record: AttendanceRecord | null;
}> {
  const record = await getTodayAttendance(userId);

  if (!record) {
    return {
      hasCheckedIn: false,
      hasCheckedOut: false,
      isAbsent: false,
      status: 'pending',
      record: null,
    };
  }

  const checkInTime = record.checkInTime instanceof Timestamp
    ? record.checkInTime.toDate()
    : record.checkInTime;

  const checkOutTime = record.checkOutTime instanceof Timestamp
    ? record.checkOutTime.toDate()
    : record.checkOutTime;

  return {
    hasCheckedIn: !!record.checkInTime,
    hasCheckedOut: !!record.checkOutTime,
    isAbsent: record.status === 'absent',
    status: record.status,
    checkInTime,
    checkOutTime,
    isLate: record.isLate,
    record,
  };
}
